import { DomainError } from '../errors/domain-error';

/** Default test sample size (% of list) for subject A/B. */
export const MARKETING_SUBJECT_AB_SAMPLE_PERCENT = 20;

/** Minimum list size to run subject A/B. */
export const MARKETING_SUBJECT_AB_MIN_RECIPIENTS = 4;

/** Minimum test-pool size (split evenly across A and B). */
export const MARKETING_SUBJECT_AB_MIN_TEST_POOL = 4;

/** Wait window before picking a winner (ms). */
export const MARKETING_SUBJECT_AB_WINDOW_MS = 4 * 60 * 60 * 1000;

export const MARKETING_SUBJECT_AB_WINNER_QUEUE = 'marketing-subject-ab-winner';

export type MarketingSubjectAbWinnerJob = {
  sendId: string;
};

export type MarketingSubjectAbVariant = 'A' | 'B';

export type MarketingSubjectAbCohortAssignment = {
  testA: string[];
  testB: string[];
  rollout: string[];
};

export type MarketingSubjectAbVariantStats = {
  verifiedOpens: number;
  delivered: number;
};

const SUBJECT_MAX = 200;

export function normalizeSubjectVariantB(
  raw: string | null | undefined,
  primarySubject: string,
): string | null {
  const variant = raw?.trim() ?? '';
  if (!variant) return null;
  if (variant.length > SUBJECT_MAX) {
    throw new DomainError(`Subject variant B must be at most ${SUBJECT_MAX} characters`);
  }
  const primary = primarySubject.trim();
  if (!primary) {
    throw new DomainError('Primary subject is required for subject A/B');
  }
  if (variant.toLowerCase() === primary.toLowerCase()) {
    throw new DomainError('Subject variant B must differ from the primary subject');
  }
  return variant;
}

export function assertSubjectAbEligible(recipientCount: number) {
  if (recipientCount < MARKETING_SUBJECT_AB_MIN_RECIPIENTS) {
    throw new DomainError(
      `Subject A/B requires at least ${MARKETING_SUBJECT_AB_MIN_RECIPIENTS} eligible subscribers`,
    );
  }
}

export function assignSubjectAbCohorts(
  recipientIds: string[],
  samplePercent = MARKETING_SUBJECT_AB_SAMPLE_PERCENT,
): MarketingSubjectAbCohortAssignment {
  assertSubjectAbEligible(recipientIds.length);
  const ids = [...recipientIds];
  for (let i = ids.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [ids[i], ids[j]] = [ids[j], ids[i]];
  }

  const percent = Math.min(100, Math.max(1, Math.floor(samplePercent)));
  let testSize = Math.max(
    MARKETING_SUBJECT_AB_MIN_TEST_POOL,
    Math.ceil((ids.length * percent) / 100),
  );
  testSize = Math.min(testSize, ids.length);
  if (testSize % 2 === 1 && testSize > 1) testSize -= 1;
  if (testSize < 2) testSize = Math.min(2, ids.length);

  const testIds = ids.slice(0, testSize);
  const rollout = ids.slice(testSize);
  const half = Math.floor(testIds.length / 2);
  return {
    testA: testIds.slice(0, half),
    testB: testIds.slice(half),
    rollout,
  };
}

export function pickSubjectAbWinner(input: {
  variantA: MarketingSubjectAbVariantStats;
  variantB: MarketingSubjectAbVariantStats;
}): MarketingSubjectAbVariant {
  const rateA =
    input.variantA.delivered > 0
      ? input.variantA.verifiedOpens / input.variantA.delivered
      : 0;
  const rateB =
    input.variantB.delivered > 0
      ? input.variantB.verifiedOpens / input.variantB.delivered
      : 0;
  if (rateB > rateA) return 'B';
  return 'A';
}

export function subjectForAbRecipient(input: {
  abVariant: string | null | undefined;
  primarySubject: string;
  variantBSubject: string | null | undefined;
  winnerSubject?: string | null;
}): string {
  if (input.abVariant === 'B' && input.variantBSubject) return input.variantBSubject;
  if (input.abVariant === 'rollout' && input.winnerSubject) return input.winnerSubject;
  if (input.abVariant === 'A') return input.primarySubject;
  return input.winnerSubject ?? input.primarySubject;
}

export const MARKETING_SUBJECT_AB_HONESTY_COPY =
  'Subject A/B winner uses verified opens from the test sample only. Raw opens are shown for transparency.';
